import { useRef, useState } from 'react';

import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface ModelPlaceholderProps {
  name: string;
  position: [number, number, number];
  onHover: (name: string | null) => void;
  onClick: (name: string) => void;
  isHovered: boolean;
}

export function ModelPlaceholder({ name, position, onHover, onClick, isHovered }: ModelPlaceholderProps) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  // Create outline effect when hovered
  useFrame(() => {
    if (meshRef.current) {
      if (hovered || isHovered) {
        meshRef.current.scale.set(1.05, 1.05, 1.05);
      } else {
        meshRef.current.scale.set(1, 1, 1);
      }
    }
  });

  // Determine color based on file extension
  const getColorFromFileName = (name: string) => {
    const extension = name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'obj':
        return '#B3EC49';
      case 'glb':
        return '#4A9FFF';
      default:
        return '#FFFFFF';
    }
  };

  return (
    <mesh
      ref={meshRef}
      position={position}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        onHover(name);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
        onHover(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(name);
      }}
    >
      {/* Use a box as placeholder for uploaded models */}
      <boxGeometry args={[1.5, 1.5, 1.5]} />
      <meshStandardMaterial
        color={getColorFromFileName(name)}
        transparent
        opacity={hovered || isHovered ? 0.8 : 0.6}
        wireframe={true}
      />
      {(hovered || isHovered) && (
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(1.7, 1.7, 1.7)]} />
          <lineBasicMaterial color="#B3EC49" />
        </lineSegments>
      )}
      <group position={[0, 0, 0]}>
        <Text color="#FFFFFF" fontSize={0.2} anchorX="center" anchorY="middle" position={[0, 0, 0.8]}>
          {name.length > 15 ? name.substring(0, 12) + '...' : name}
        </Text>
      </group>
    </mesh>
  );
}
